// Constant representing shared degrees_of_freedom_spec for any creative type.
export const DEGREES_OF_FREEDOM_SPEC = {
    creative_features_spec: {
      advantage_plus_creative: { enroll_status: "OPT_OUT" },
      enhance_cta: { enroll_status: "OPT_OUT" },
      image_animation: { enroll_status: "OPT_OUT" },
      image_brightness_and_contrast: { enroll_status: "OPT_OUT" },
      image_templates: { enroll_status: "OPT_OUT" },
      image_touchups: { enroll_status: "OPT_OUT" },
      inline_comment: { enroll_status: "OPT_OUT" },
      pac_relaxation: { enroll_status: "OPT_OUT" },
      product_extensions: { enroll_status: "OPT_OUT" },
      site_extensions: { enroll_status: "OPT_OUT" },
      // standard_enhancements: { enroll_status: "OPT_OUT" },
      text_optimizations: {
        enroll_status: "OPT_OUT",
        customizations: {
          text_extraction: { enroll_status: "OPT_OUT" },
        },
      },
      video_auto_crop: { enroll_status: "OPT_OUT" },
      video_filtering: { enroll_status: "OPT_OUT" },
    },
  }
  
  /**
   * Asynchronous function to create a JSON payload for a video ad creative.
   *
   * @param {Object} params - Parameters for the creative.
   * @param {string} params.name - Name of the ad creative.
   * @param {Array} params.bodies - Array of body objects. Example: [{ text: 'Some text' }, ...]
   * @param {Array} params.titles - Array of title objects.
   * @param {string} params.pageId - Facebook page id.
   * @param {string} params.instagramActorId - Instagram actor id.
   * @param {string} params.videoId - ID of the video to use in video_data.
   * @param {Object} params.callToAction - Call to action object.
   * @param {string} params.thumbnailUrl - Cover image URL for the video.
   *
   * @returns {Object} - JSON payload to create a Facebook ad creative.
   */
  export function createSingleVideoAdCreativePayload(params) {
    const {
      name,
      bodies,
      titles,
      pageId,
      instagramActorId,
      videoId,
      callToActionType,
      callToActionLink,
      thumbnailUrl,
      trackingTemplate=null,
      deepLink=null,
    } = params
  
    // Build the asset_feed_spec part of the payload.
    const assetFeedSpec = {
      bodies, // array of body objects
      titles, // array of title objects
      optimization_type: "DEGREES_OF_FREEDOM",
    }
  
    // Build the object_story_spec with embedded video data.
    const objectStorySpec = {
      page_id: pageId,
      instagram_user_id: instagramActorId,
      video_data: {
        video_id: videoId,
        call_to_action: {
            type: callToActionType,
            value: { 
              link: callToActionLink, 
              ...(deepLink && { app_link: deepLink })
            },
        },
        image_url: thumbnailUrl,
      },
    }
  
    // Build the payload.
    const payload = {
      name: name,
      ...(trackingTemplate && { url_tags: trackingTemplate }),
      asset_feed_spec: assetFeedSpec,
      degrees_of_freedom_spec: DEGREES_OF_FREEDOM_SPEC,
      object_story_spec: objectStorySpec,
    }

    if (bodies.length === 1 && titles.length === 1) {
      payload.object_story_spec.video_data.message = bodies[0].text
      payload.object_story_spec.video_data.title = titles[0].text
      delete payload.asset_feed_spec
    }
  
    // Return the constructed payload (async wrapper enables potential future async tasks)
    return payload
  }

/**
 * // Example params object for a video ad creative payload
const exampleParams = {
  name: "postman_test_vid_creative_cdn",
  bodies: [
    {
      text: "Professional, functional luggage that keeps your tech safe and your style intact."
    },
    {
      text: "Sleek polycarbonate cabin luggage with dedicated tech compartment for seamless business travel."
    },
    {
      text: "Charging on-the-go, impact-resistant design, and smooth Hinomoto wheels for the modern traveler."
    }
  ],
  descriptions: [
    {
      text: "Unbreakable & lightweightMade of polycarbonate hard shell that is flexible & strong. Magic in the detailsGroove details on the luggage fascia enhance its strength. Super silent ninja wheels8 smooth Japanese Hinomoto wheels that glide across carpets and squeaky floors. The yellow detail makes..."
    }
  ],
  titles: [
    {
      text: "Travel Smart, Travel Stylish"
    },
    {
      text: "Hustle Without Hassle"
    },
    {
      text: "Tech-Ready Luggage Revolution"
    }
  ],
  optimizationType: "DEGREES_OF_FREEDOM",
  pageId: "278876351972107",
  instagramActorId: "7537925496257925",
  videoId: "2113710705742639",
  callToActionType: "SHOP_NOW",
  callToActionLink: "https://mokobara.com/products/the-transit-cabin-luggage",
  thumbnailUrl: "https://scontent.fblr21-1.fna.fbcdn.net/v/t15.5256-10/476296263_920493176558438_7261006719900098691_n.jpg"
};
 */

/**
 * Asynchronous function to create a JSON payload for an image ad creative.
 * This payload includes all required asset_feed_spec components, degrees_of_freedom_spec,
 * and object_story_spec with link_data.
 *
 * @param {Object} params - Parameters for the creative.
 * @param {string} params.name - Name of the ad creative.
 * @param {Array} params.bodies - Array of body objects. Example: [{ text: 'Some text' }, ...]
 * @param {Array} params.titles - Array of title objects.
 * @param {string} params.pageId - Facebook page id.
 * @param {string} params.instagramActorId - Instagram actor id.
 * @param {string} params.imageHash - Image hash provided by Facebook.
 * @param {string} params.callToActionLink - URL for the link in the ad.
 * @param {string} params.callToActionType - Call-to-action type (e.g., "SHOP_NOW").
 *
 * @returns {Object} - JSON payload to create a Facebook image ad creative.
 */
export function createSingleImageAdCreativePayload(params) {
    // console.log("createSingleImageAdCreativePayload", params)
    const {
      name,
      bodies,
      titles,
      pageId,
      instagramActorId,
      imageHash,
      callToActionLink,
      callToActionType,
      trackingTemplate=null,
      deepLink=null,
    } = params
  
    // Build the asset_feed_spec part of the payload.
    const assetFeedSpec = {
      bodies,           // array of body objects
      titles,           // array of title objects
      optimization_type: "DEGREES_OF_FREEDOM",
    }
  
    // Build the object_story_spec containing link data.
    const objectStorySpec = {
      page_id: pageId,
      instagram_user_id: instagramActorId,
      link_data: {
        link: callToActionLink,
        caption: callToActionLink,
        image_hash: imageHash,
        call_to_action: {
          type: callToActionType,
          value: { 
            link: callToActionLink, 
            ...(deepLink && { app_link: deepLink })
          }
        },
      },
    }
  
    // Construct the full payload.
    const payload = {
      name: name,
      ...(trackingTemplate && { url_tags: trackingTemplate }),
      asset_feed_spec: assetFeedSpec,
      degrees_of_freedom_spec: DEGREES_OF_FREEDOM_SPEC,
      object_story_spec: objectStorySpec,
    }

    // check if len of bodies / titles is 1, then we can skip the assetFeedSpec and add the text directly to objectStorySpec
    if (bodies.length === 1 && titles.length === 1) {
      payload.object_story_spec.link_data.message = bodies[0].text
      payload.object_story_spec.link_data.name = titles[0].text
      delete payload.asset_feed_spec
    }

    // console.log("createSingleImageAdCreativePayload", payload)
  
    return payload
  }

/**
 * // Example params object for an image ad creative payload
 * const exampleParams = {
  name: "postman_test_img_creative_cdn",
  bodies: [
    {
      text: "Professional, functional luggage that keeps your tech safe and your style intact."
    },
    {
      text: "Sleek polycarbonate cabin luggage with dedicated tech compartment for seamless business travel."
    },
    {
      text: "Charging on-the-go, impact-resistant design, and smooth Hinomoto wheels for the modern traveler."
    }
  ],
  descriptions: [
    {
      text: "Unbreakable & lightweightMade of polycarbonate hard shell that is flexible & strong. Magic in the detailsGroove details on the luggage fascia enhance its strength. Super silent ninja wheels8 smooth Japanese Hinomoto wheels that glide across carpets and squeaky floors. The yellow detail makes..."
    }
  ],
  titles: [
    {
      text: "Travel Smart, Travel Stylish"
    },
    {
      text: "Hustle Without Hassle"
    },
    {
      text: "Tech-Ready Luggage Revolution"
    }
  ],
  optimizationType: "DEGREES_OF_FREEDOM",
  pageId: "278876351972107",
  instagramActorId: "7537925496257925",
  link: "https://mokobara.com/products/the-transit-cabin-luggage",
  caption: "Shop Now",
  imageHash: "HASH",
  callToActionType: "SHOP_NOW"
};
    */  
